/*
  CODEX CUSTOMIZATION START
  File: custom-wishlist-hydration.js
  Purpose:
  - Hydrate Wishlist Power's guest/customer wishlist outside the wishlist page.
  - Update existing Wishlist Power count and heart controls without per-card requests.
  CODEX CUSTOMIZATION END
*/

(() => {
  const WISHLIST_READY_EVENT = 'ooo-wishlist:ready';
  const COUNT_SELECTOR = 'ooo-wl-wishlist-count';
  const CARD_BUTTON_SELECTOR = 'ooo-wl-product-card-button';
  const CLICKABLE_BUTTON_SELECTOR = 'ooo-wl-product-card-button, ooo-wl-wishlist-button';
  const API_WAIT_TIMEOUT = 4000;
  const HYDRATION_KEY = '__codexWishlistHydrationStarted';
  const HYDRATION_RETRY_DELAYS = [0, 250, 1000, 2500];
  const INTERACTION_REFRESH_DELAY = 1200;
  const hydrationState = {
    hydrationGeneration: 0,
    hydrationTimers: [],
  };
  let interactionRefreshTimer = null;

  function getWishlistApi() {
    const wishlistApi = window.Maestrooo?.wishlist;

    return wishlistApi && typeof wishlistApi.getContent === 'function' ? wishlistApi : null;
  }

  function waitForWishlistApi() {
    const api = getWishlistApi();

    if (api) {
      return Promise.resolve(api);
    }

    return new Promise((resolve) => {
      let timeoutId;

      const complete = () => {
        window.clearTimeout(timeoutId);
        document.removeEventListener(WISHLIST_READY_EVENT, complete);
        resolve(getWishlistApi());
      };

      document.addEventListener(WISHLIST_READY_EVENT, complete, { once: true });
      timeoutId = window.setTimeout(complete, API_WAIT_TIMEOUT);
    });
  }

  function refreshWishlistCount() {
    document.querySelectorAll(COUNT_SELECTOR).forEach((countElement) => {
      // Wishlist Power refreshes its own count when an existing count attribute is removed.
      // The API result is now cached, so this is a local read rather than another request.
      if (!countElement.hasAttribute('count')) {
        countElement.setAttribute('count', '0');
      }

      countElement.removeAttribute('count');
    });
  }

  function getWishlistButtonControl(buttonElement) {
    return buttonElement.wishlistButton
      || buttonElement.button
      || buttonElement.shadowRoot?.querySelector('button')
      || buttonElement.querySelector('button');
  }

  function getWishlistButtonHandle(buttonElement) {
    if (typeof buttonElement.handle === 'string' && buttonElement.handle !== '') {
      return buttonElement.handle;
    }

    return buttonElement.getAttribute('handle');
  }

  function getWishlistButtonProductTitle(buttonElement) {
    const explicitTitle = buttonElement.getAttribute('product-title')
      || buttonElement.dataset.productTitle;

    if (explicitTitle) {
      return explicitTitle.trim();
    }

    const cardTitle = buttonElement
      .closest('product-card')
      ?.querySelector('.product-card__title a')
      ?.textContent
      ?.trim();

    return cardTitle || '';
  }

  function buildWishlistButtonLabel(action, productTitle) {
    return productTitle ? `${action}: ${productTitle}` : action;
  }

  function clearWishlistHydrationTimers(state) {
    state.hydrationTimers.forEach((timerId) => {
      window.clearTimeout(timerId);
    });

    state.hydrationTimers = [];
  }

  function cancelWishlistHydrationRetries() {
    hydrationState.hydrationGeneration += 1;
    clearWishlistHydrationTimers(hydrationState);

    if (interactionRefreshTimer) {
      window.clearTimeout(interactionRefreshTimer);
      interactionRefreshTimer = null;
    }
  }

  function scheduleInteractionRefresh() {
    cancelWishlistHydrationRetries();

    interactionRefreshTimer = window.setTimeout(() => {
      interactionRefreshTimer = null;
      queueHydration({ forceFresh: true });
    }, INTERACTION_REFRESH_DELAY);
  }

  function normalizeWishlistToggleButton(buttonElement, control, isSaved, productTitle, translations) {
    const actionLabel = isSaved
      ? (translations.remove_from_wishlist || 'Remove from wishlist')
      : (translations.add_to_wishlist || 'Add to wishlist');
    const label = buildWishlistButtonLabel(actionLabel, productTitle);
    const pressed = String(isSaved);

    [buttonElement, control].forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      element.setAttribute('role', 'button');
      element.setAttribute('aria-pressed', pressed);
      element.setAttribute('aria-label', label);
      element.removeAttribute('aria-checked');
    });
  }

  function refreshWishlistButtons(products) {
    const savedHandles = new Set(
      products
        .map((product) => product?.handle)
        .filter((handle) => typeof handle === 'string' && handle !== '')
    );
    const translations = window.ooo?.wishlist?.translations || {};

    document.querySelectorAll(CARD_BUTTON_SELECTOR).forEach((buttonElement) => {
      const handle = getWishlistButtonHandle(buttonElement);
      const control = getWishlistButtonControl(buttonElement);
      const productTitle = getWishlistButtonProductTitle(buttonElement);

      if (!handle || !control) {
        return;
      }

      const isSaved = savedHandles.has(handle);

      normalizeWishlistToggleButton(buttonElement, control, isSaved, productTitle, translations);
      control.removeAttribute('loading');
      buttonElement.removeAttribute('loading');
    });
  }

  async function hydrateWishlist(state, { forceFresh = false, generation = 0 } = {}) {
    if (generation !== state.hydrationGeneration) {
      return;
    }

    const wishlistApi = await waitForWishlistApi();

    if (generation !== state.hydrationGeneration) {
      return;
    }

    if (!wishlistApi) {
      return;
    }

    try {
      // On first load we can use the app cache, but interaction-driven refreshes
      // should always ask for a fresh read so a recent add/remove does not roll back.
      const content = await wishlistApi.getContent(forceFresh ? false : true);
      const products = Array.isArray(content?.products) ? content.products : [];

      if (generation !== state.hydrationGeneration) {
        return;
      }

      refreshWishlistCount();
      refreshWishlistButtons(products);
    } catch (error) {
      // Wishlist controls remain usable if the optional background hydration fails.
      console.debug('[Wishlist Power] Background wishlist hydration was unavailable.', error);
    }
  }

  function queueHydration({ forceFresh = false } = {}) {
    if (window[HYDRATION_KEY] && !forceFresh) {
      return;
    }

    if (!window[HYDRATION_KEY]) {
      window[HYDRATION_KEY] = true;
    }

    hydrationState.hydrationGeneration += 1;
    const generation = hydrationState.hydrationGeneration;

    clearWishlistHydrationTimers(hydrationState);

    const runHydration = () => {
      hydrateWishlist(hydrationState, { forceFresh, generation });
    };

    const runWhenIdle = () => {
      if (generation !== hydrationState.hydrationGeneration) {
        return;
      }

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(runHydration, { timeout: 1200 });
      } else {
        window.setTimeout(runHydration, 0);
      }
    };

    if (document.readyState === 'complete') {
      runWhenIdle();
    } else {
      window.addEventListener('load', runWhenIdle, { once: true });
    }

    if (!forceFresh) {
      HYDRATION_RETRY_DELAYS.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          if (generation !== hydrationState.hydrationGeneration) {
            return;
          }

          runHydration();
        }, delay);

        hydrationState.hydrationTimers.push(timerId);
      });
    }
  }

  queueHydration();

  ['ooo-wishlist:product-added', 'ooo-wishlist:product-removed'].forEach((eventName) => {
    document.addEventListener(eventName, () => scheduleInteractionRefresh());
  });

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target instanceof Element
      ? event.target.closest(CLICKABLE_BUTTON_SELECTOR)
      : null;

    if (!wishlistButton) {
      return;
    }

    cancelWishlistHydrationRetries();
  }, true);
})();
